import React, { useState, useMemo } from "react";
import PositionInput from "./PositionInput";
import KeySelector from "./KeySelector";
import AnnotationEntry from "./AnnotationEntry";
import AddButton from "./AddButton";
import AnnotationModal from "./AnnotationModal";
import { useAnnotation } from "../contexts/AnnotationContext";
import { IconButton, Collapse } from "@mui/material";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import { useViewMode } from "../contexts/ViewModeContext";
import { calculateMaxSubdivisions, calculateMaxBeats, getTimeSignatureForBar } from "../utils/subdivisionUtils";
import InfoTooltip from "./InfoTooltip";
import { KeyInstruction } from "../lib/definitions/SongLevelAnnotation";

/**
 * KeyChangeSection component for adding and editing key changes
 * Updated to be collapsible and take less space
 */
const KeyChangeSection = ({ 
  keyChanges, 
  onChange,
}) => {
  const { 
    generalInfo,
    timeSignatures,
  } = useAnnotation();
  const measures = generalInfo.duration_number_of_bars;
  const [showForm, setShowForm] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [newKeyChange, setNewKeyChange] = useState({
    position: { bar: 1, beat: 1, subdivision: 1 },
    value: { note: 'C', accidental: '', mode: 'Major' }
  });
  const [error, setError] = useState("");

  // View mode
  const {isViewMode} = useViewMode();

  // Calculate max subdivisions based on current bar's time signature
  const maxSubdivision = useMemo(() => {
    const currentBar = newKeyChange.position.bar;
    if (!currentBar || isNaN(currentBar)) return 4; // Default fallback
    
    const timeSignature = getTimeSignatureForBar(currentBar, timeSignatures);
    return calculateMaxSubdivisions(timeSignature);
  }, [newKeyChange.position.bar, timeSignatures]);

  // Calculate max beats based on current bar's time signature
  const maxBeat = useMemo(() => {
    const currentBar = newKeyChange.position.bar;
    if (!currentBar || isNaN(currentBar)) return 4; // Default fallback
    
    const timeSignature = getTimeSignatureForBar(currentBar, timeSignatures);
    return calculateMaxBeats(timeSignature);
  }, [newKeyChange.position.bar, timeSignatures]);

  const handleAddClick = () => {
    setNewKeyChange({
      position: { bar: 1, beat: 1, subdivision: 1 },
      value: { note: 'C', accidental: '', mode: 'Major' }
    });
    setEditingIndex(null);
    setShowForm(true);
    setExpanded(true); // Auto-expand when adding
  };

  const handleEditClick = (index) => {
    const keyChange = keyChanges[index];
    setNewKeyChange({
      position: { ...keyChange.position },
      value: { ...keyChange.value }
    });
    setEditingIndex(index);
    setShowForm(true);
    setExpanded(true); // Auto-expand when editing
  };

  const handlePositionChange = (position) => {
    setNewKeyChange({ ...newKeyChange, position });
  };

  const handleKeyChange = (value) => {
    setNewKeyChange({ ...newKeyChange, value });
  };

  const handleSave = () => {
    // Clear previous errors
    setError("");
    
    // COMMENTED OUT: Measure range validation
    // Convert measure to number and validate
    // const measureNum = newKeyChange.position.bar;
    // if (isNaN(measureNum) || measureNum < 1 || measureNum > measures) {
    //   setError(`Please enter a valid measure number between 1 and ${measures}`);
    //   return;
    // }

    // COMMENTED OUT: Beat validation based on time signature
    // Check correct beat number for time signatures
    // const timeSig = getTimeSignatureForBar(measureNum, timeSignatures);
    // const beatsInBar = parseInt(timeSig.split("/")[0], 10);   // numerator
    // const beatNum   = newKeyChange.position.beat;
  
    // if (isNaN(beatNum) || beatNum < 1 || beatNum > beatsInBar) {
    //   setError(
    //     `Beat must be between 1 and ${beatsInBar} for bar ${measureNum} (time signature ${timeSig}).`
    //   );
    //   return;
    // }

    // COMMENTED OUT: Subdivision validation based on time signature
    // Check subdivision limits based on time signature
    // const maxSubdivisionsForBar = calculateMaxSubdivisions(timeSig);
    // const subdivisionNum = newKeyChange.position.subdivision;
    
    // if (isNaN(subdivisionNum) || subdivisionNum < 1 || subdivisionNum > maxSubdivisionsForBar) {
    //   setError(
    //     `Subdivision must be between 1 and ${maxSubdivisionsForBar} for bar ${measureNum} (time signature ${timeSig}).`
    //   );
    //   return;
    // }

    // COMMENTED OUT: Chronological order validation
    // Make sure new changes are in chronological order
    // const posToNumber = ({ bar, beat, subdivision }) =>
    //   bar * 10_000 + beat * 100 + subdivision;   // assumes <100 beats & <100 subdivisions

    // const newPos = posToNumber(newKeyChange.position);

    // const latestPos = keyChanges
    //   .filter((_, idx) => idx !== editingIndex)
    //   .reduce(
    //     (max, kc) => Math.max(max, posToNumber(kc.position)),
    //     0
    //   );

    // if (newPos <= latestPos) {
    //   setError(
    //     "Key changes must be added in chronologically increasing order."
    //   );
    //   return;
    // }

    const updated = [...keyChanges];
    if (editingIndex != null) updated[editingIndex] = newKeyChange;
    else updated.push(newKeyChange);
    updated.sort((a,b) => a.position.bar - b.position.bar);
    onChange(updated);
    
    // Close form if successful
    setShowForm(false);
  };

  const handleCancel = () => {
    setShowForm(false);
  };

  const handleDelete = (index) => {
    const updated = keyChanges.filter((_,i) => i !== index);
    onChange(updated);
  };

  const formatPosition = (pos) => {
    return `${pos.bar}:${pos.beat}:${pos.subdivision}`;
  };

  const toggleExpand = () => {
    setExpanded(!expanded);
  };

  // Get a summary of key changes for the collapsed view
  const getSummary = () => {
    if (keyChanges.length === 0) {
      return "No key changes";
    } else if (keyChanges.length === 1) {
      const kc = keyChanges[0];
      return `1 key change: ${kc.value.note}${kc.value.accidental} ${kc.value.mode} at ${formatPosition(kc.position)}`;
    } else {
      return `${keyChanges.length} key changes`;
    }
  };

  return (
    <div className="mb-4 rounded-md overflow-hidden">
      {/* Header with expand/collapse */}
      <div 
        className="flex items-center px-3 py-2 bg-gray-50 cursor-pointer"
        onClick={toggleExpand}
      >
        <h3 className="text-base font-medium m-0"> Key (曲调) </h3>
        <div className="flex items-center ml-auto">
          <span className="text-sm text-gray-500 mr-2">{!expanded && getSummary()}</span>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); toggleExpand(); }}>
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </div>
        <InfoTooltip instruction={KeyInstruction}/>
      </div>
      
      {/* Collapsible content */}
      <Collapse in={expanded}>
        <div className="px-3 py-2">
          {/* List of existing key changes */}
          <div className="flex flex-wrap items-center" >
            {keyChanges.map((keyChange, index) => (
              <div key={index} className="mr-2 mb-2">
                <AnnotationEntry
                  label={`${formatPosition(keyChange.position)} ${keyChange.value.note}${keyChange.value.accidental} ${keyChange.value.mode}`}
                  onDelete={() => handleDelete(index)}
                  onClick={() => handleEditClick(index)}
                />
              </div>
            ))}
            {!isViewMode && <AddButton onClick={handleAddClick} />}
          </div>
        </div>
      </Collapse>
      
      {/* Modal for adding/editing */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <AnnotationModal
            title="Key (曲调)"
            onSave={handleSave}
            onCancel={handleCancel}
            isEditing={editingIndex !== null}
            error={error}
          >
            <div className="mb-4">
              <div className="mb-3">
                <label className="block text-sm mb-1">Position</label>
                <PositionInput 
                  position={newKeyChange.position} 
                  onChange={handlePositionChange}
                  maxSubdivision={maxSubdivision}
                  maxBeat={maxBeat}
                />
              </div>
              
              <div className="mb-3">
                <label className="block text-sm mb-1">Key</label>
                <KeySelector value={newKeyChange.value} onChange={handleKeyChange} />
              </div>
            </div>
          </AnnotationModal>
        </div>
      )}
    </div>
  );
};

export default KeyChangeSection;
